import React, { useState, useEffect } from 'react';
import {
  Card,
  Page,
  Layout,
  DataTable,
  Badge,
  Modal,
  Toast,
  Frame,
  Loading,
  EmptyState,
  Select,
  LegacyStack as Stack,
  TextContainer,
  Button,
  ButtonGroup,
  Filters,
  ChoiceList,
  TextField,
  DatePicker,
  Popover,
  Icon,
} from '@shopify/polaris';

type PaymentStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

interface Payment {
  id: string;
  whatsappPaymentId: string;
  customer: {
    name: string;
    phone: string;
  };
  order?: {
    id: string;
    orderNumber: string;
  };
  amount: number;
  currency: string;
  status: PaymentStatus;
  transactionId?: string;
  errorMessage?: string;
  createdAt: string;
  paidAt?: string;
  canceledAt?: string;
}

interface PaymentAnalytics {
  totalPayments: number;
  totalAmount: number;
  completedCount: number;
  completedAmount: number;
  pendingCount: number;
  pendingAmount: number;
  failedCount: number;
  failedAmount: number;
  successRate: number;
}

interface DailyPaymentData {
  date: string;
  total: number;
  completed: number;
  pending: number;
  failed: number;
  totalAmount: number;
  completedAmount: number;
}

interface PaymentsDashboardProps {
  storeId: string;
  onError?: (error: string) => void;
}

export const PaymentsDashboard: React.FC<PaymentsDashboardProps> = ({ storeId, onError }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [analytics, setAnalytics] = useState<PaymentAnalytics | null>(null);
  const [dailyData, setDailyData] = useState<DailyPaymentData[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<{ start?: Date; end?: Date }>({});
  const [datePickerActive, setDatePickerActive] = useState(false);

  // Modal states
  const [detailsModalActive, setDetailsModalActive] = useState(false);
  const [updateModalActive, setUpdateModalActive] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<PaymentStatus>('COMPLETED');
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  // Toast state
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);

  useEffect(() => {
    loadPayments();
    loadAnalytics();
    loadDailyData();
  }, [statusFilter, offset]);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (statusFilter.length > 0) {
        params.append('status', statusFilter[0]);
      }

      const response = await fetch(`/api/payments?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load payments');

      const data = await response.json();
      setPayments(data.data.payments || []);
      setTotal(data.data.total || 0);
    } catch (error: any) {
      showToast(error.message || 'Failed to load payments', true);
      onError?.(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.start) {
        params.append('startDate', dateRange.start.toISOString());
      }
      if (dateRange.end) {
        params.append('endDate', dateRange.end.toISOString());
      }

      const response = await fetch(`/api/payments/analytics/summary?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load analytics');

      const data = await response.json();
      setAnalytics(data.data);
    } catch (error: any) {
      console.error('Failed to load analytics:', error);
    }
  };

  const loadDailyData = async () => {
    try {
      const params = new URLSearchParams({ days: '30' });

      const response = await fetch(`/api/payments/analytics/daily?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load daily data');

      const data = await response.json();
      setDailyData(data.data.dailyPayments || []);
    } catch (error: any) {
      console.error('Failed to load daily data:', error);
    }
  };

  const updatePaymentStatus = async () => {
    if (!selectedPayment) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/payments/${selectedPayment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: updateStatus,
          errorMessage: errorMessage || undefined,
        }),
      });

      if (!response.ok) throw new Error('Failed to update payment');

      showToast('Payment updated successfully');
      setUpdateModalActive(false);
      loadPayments();
      loadAnalytics();
    } catch (error: any) {
      showToast(error.message || 'Failed to update payment', true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter([]);
    setSearchQuery('');
    setDateRange({});
    setOffset(0);
  };

  const showToast = (message: string, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  };

  const getBadgeStatus = (
    status: string
  ): 'success' | 'warning' | 'info' | 'attention' | 'critical' => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'attention';
      case 'PROCESSING':
        return 'info';
      case 'FAILED':
        return 'critical';
      case 'CANCELLED':
        return 'warning';
      default:
        return 'info';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const filteredPayments = payments.filter((payment) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        payment.customer.name?.toLowerCase().includes(query) ||
        payment.customer.phone.toLowerCase().includes(query) ||
        payment.whatsappPaymentId.toLowerCase().includes(query) ||
        payment.transactionId?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const paymentRows = filteredPayments.map((payment) => [
    payment.customer.name || payment.customer.phone,
    payment.customer.phone,
    payment.order?.orderNumber || 'N/A',
    formatCurrency(Number(payment.amount), payment.currency),
    <Badge status={getBadgeStatus(payment.status)}>{payment.status}</Badge>,
    payment.transactionId || 'N/A',
    new Date(payment.createdAt).toLocaleString(),
    payment.paidAt ? new Date(payment.paidAt).toLocaleString() : 'N/A',
    <ButtonGroup>
      <Button
        size="slim"
        onClick={() => {
          setSelectedPayment(payment);
          setDetailsModalActive(true);
        }}
      >
        Details
      </Button>
      <Button
        size="slim"
        onClick={() => {
          setSelectedPayment(payment);
          setUpdateStatus(payment.status);
          setErrorMessage(payment.errorMessage || '');
          setUpdateModalActive(true);
        }}
      >
        Update
      </Button>
    </ButtonGroup>,
  ]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Pending', value: 'PENDING' },
            { label: 'Processing', value: 'PROCESSING' },
            { label: 'Completed', value: 'COMPLETED' },
            { label: 'Failed', value: 'FAILED' },
            { label: 'Cancelled', value: 'CANCELLED' },
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = statusFilter.length > 0
    ? [
        {
          key: 'status',
          label: `Status: ${statusFilter[0]}`,
          onRemove: () => setStatusFilter([]),
        },
      ]
    : [];

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastActive(false)}
      duration={3000}
    />
  ) : null;

  return (
    <Frame>
      <Page title="WhatsApp Payments">
        <Layout>
          {/* Analytics Summary */}
          {analytics && (
            <Layout.Section>
              <Stack distribution="fillEvenly">
                <Card sectioned>
                  <Stack vertical spacing="tight">
                    <TextContainer>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
                        {formatCurrency(analytics.totalAmount)}
                      </p>
                      <p style={{ color: '#6d7175' }}>Total Amount</p>
                      <p style={{ fontSize: '0.85rem', color: '#6d7175' }}>
                        {analytics.totalPayments} payments
                      </p>
                    </TextContainer>
                  </Stack>
                </Card>

                <Card sectioned>
                  <Stack vertical spacing="tight">
                    <TextContainer>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#008060' }}>
                        {formatCurrency(analytics.completedAmount)}
                      </p>
                      <p style={{ color: '#6d7175' }}>Completed</p>
                      <p style={{ fontSize: '0.85rem', color: '#6d7175' }}>
                        {analytics.completedCount} payments
                      </p>
                    </TextContainer>
                  </Stack>
                </Card>

                <Card sectioned>
                  <Stack vertical spacing="tight">
                    <TextContainer>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#ffa500' }}>
                        {formatCurrency(analytics.pendingAmount)}
                      </p>
                      <p style={{ color: '#6d7175' }}>Pending</p>
                      <p style={{ fontSize: '0.85rem', color: '#6d7175' }}>
                        {analytics.pendingCount} payments
                      </p>
                    </TextContainer>
                  </Stack>
                </Card>

                <Card sectioned>
                  <Stack vertical spacing="tight">
                    <TextContainer>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
                        {analytics.successRate.toFixed(1)}%
                      </p>
                      <p style={{ color: '#6d7175' }}>Success Rate</p>
                      <p style={{ fontSize: '0.85rem', color: '#d72c0d' }}>
                        {analytics.failedCount} failed
                      </p>
                    </TextContainer>
                  </Stack>
                </Card>
              </Stack>
            </Layout.Section>
          )}

          {/* Payments List */}
          <Layout.Section>
            <Card>
              <Card.Section>
                <Filters
                  queryValue={searchQuery}
                  queryPlaceholder="Search by customer, phone, or transaction ID"
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onQueryChange={setSearchQuery}
                  onQueryClear={() => setSearchQuery('')}
                  onClearAll={handleClearFilters}
                />
              </Card.Section>

              {loading && payments.length === 0 ? (
                <Loading />
              ) : filteredPayments.length === 0 ? (
                <EmptyState
                  heading="No payments found"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    {payments.length === 0
                      ? 'Payments will appear here when customers complete WhatsApp payments.'
                      : 'Try adjusting your filters.'}
                  </p>
                </EmptyState>
              ) : (
                <>
                  <DataTable
                    columnContentTypes={[
                      'text',
                      'text',
                      'text',
                      'numeric',
                      'text',
                      'text',
                      'text',
                      'text',
                      'text',
                    ]}
                    headings={[
                      'Customer',
                      'Phone',
                      'Order',
                      'Amount',
                      'Status',
                      'Transaction ID',
                      'Created',
                      'Paid At',
                      'Actions',
                    ]}
                    rows={paymentRows}
                  />
                  <Card.Section>
                    <Stack distribution="center">
                      <Button
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                      >
                        Previous
                      </Button>
                      <TextContainer>
                        <p>
                          Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
                        </p>
                      </TextContainer>
                      <Button
                        disabled={offset + limit >= total}
                        onClick={() => setOffset(offset + limit)}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Card.Section>
                </>
              )}
            </Card>
          </Layout.Section>

          {/* Daily Chart Info */}
          <Layout.Section secondary>
            <Card title="Payment Insights" sectioned>
              <TextContainer>
                <p>
                  Track WhatsApp payment transactions, monitor success rates, and manage
                  failed payments.
                </p>
                <p>
                  <strong>Note:</strong> WhatsApp payments are currently only available
                  in India with UPI.
                </p>
              </TextContainer>
            </Card>

            {dailyData.length > 0 && (
              <Card title="Last 30 Days" sectioned>
                <Stack vertical>
                  {dailyData.slice(-7).map((day) => (
                    <div key={day.date}>
                      <Stack distribution="equalSpacing">
                        <TextContainer>
                          <p style={{ fontSize: '0.85rem' }}>
                            {new Date(day.date).toLocaleDateString()}
                          </p>
                        </TextContainer>
                        <TextContainer>
                          <p style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                            {formatCurrency(day.completedAmount)}
                          </p>
                        </TextContainer>
                      </Stack>
                    </div>
                  ))}
                </Stack>
              </Card>
            )}
          </Layout.Section>
        </Layout>

        {/* Payment Details Modal */}
        <Modal
          open={detailsModalActive}
          onClose={() => setDetailsModalActive(false)}
          title="Payment Details"
          secondaryActions={[
            {
              content: 'Close',
              onAction: () => setDetailsModalActive(false),
            },
          ]}
        >
          {selectedPayment && (
            <Modal.Section>
              <Stack vertical>
                <TextContainer>
                  <p>
                    <strong>WhatsApp Payment ID:</strong> {selectedPayment.whatsappPaymentId}
                  </p>
                  <p>
                    <strong>Customer:</strong> {selectedPayment.customer.name || 'N/A'}
                  </p>
                  <p>
                    <strong>Phone:</strong> {selectedPayment.customer.phone}
                  </p>
                  <p>
                    <strong>Amount:</strong>{' '}
                    {formatCurrency(Number(selectedPayment.amount), selectedPayment.currency)}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Badge status={getBadgeStatus(selectedPayment.status)}>
                      {selectedPayment.status}
                    </Badge>
                  </p>
                  {selectedPayment.order && (
                    <p>
                      <strong>Order:</strong> {selectedPayment.order.orderNumber}
                    </p>
                  )}
                  {selectedPayment.transactionId && (
                    <p>
                      <strong>Transaction ID:</strong> {selectedPayment.transactionId}
                    </p>
                  )}
                  <p>
                    <strong>Created:</strong>{' '}
                    {new Date(selectedPayment.createdAt).toLocaleString()}
                  </p>
                  {selectedPayment.paidAt && (
                    <p>
                      <strong>Paid At:</strong>{' '}
                      {new Date(selectedPayment.paidAt).toLocaleString()}
                    </p>
                  )}
                  {selectedPayment.canceledAt && (
                    <p>
                      <strong>Cancelled At:</strong>{' '}
                      {new Date(selectedPayment.canceledAt).toLocaleString()}
                    </p>
                  )}
                  {selectedPayment.errorMessage && (
                    <p style={{ color: '#d72c0d' }}>
                      <strong>Error:</strong> {selectedPayment.errorMessage}
                    </p>
                  )}
                </TextContainer>
              </Stack>
            </Modal.Section>
          )}
        </Modal>

        {/* Update Payment Modal */}
        <Modal
          open={updateModalActive}
          onClose={() => setUpdateModalActive(false)}
          title="Update Payment Status"
          primaryAction={{
            content: 'Update',
            onAction: updatePaymentStatus,
            loading,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setUpdateModalActive(false),
            },
          ]}
        >
          <Modal.Section>
            <Stack vertical>
              <Select
                label="Status"
                options={[
                  { label: 'Pending', value: 'PENDING' },
                  { label: 'Processing', value: 'PROCESSING' },
                  { label: 'Completed', value: 'COMPLETED' },
                  { label: 'Failed', value: 'FAILED' },
                  { label: 'Cancelled', value: 'CANCELLED' },
                ]}
                value={updateStatus}
                onChange={(value) => setUpdateStatus(value as PaymentStatus)}
              />

              <TextField
                label="Error Message (optional)"
                value={errorMessage}
                onChange={setErrorMessage}
                multiline={2}
                placeholder="Reason for failure or cancellation"
                autoComplete="off"
              />
            </Stack>
          </Modal.Section>
        </Modal>

        {toastMarkup}
      </Page>
    </Frame>
  );
};
